import { createContext, PropsWithChildren, useCallback, useContext, useMemo, useRef } from "react"
import { useQueryClient } from "@tanstack/react-query"
import FullCalendar from "@fullcalendar/react"
import { ConfigQuery } from "../../../createOrderApi"

const RefreshCalendarContext = createContext<{
    setCalendarRef: (ref: FullCalendar | null) => void
    triggerRefetch: () => void
} | null>(null)

type Props = Readonly<PropsWithChildren<{}>>

export function RefreshCalendarProvider({ children }: Props) {
    const calendarRef = useRef<FullCalendar | null>(null)
    const queryClient = useQueryClient()

    const setCalendarRef = useCallback((ref: FullCalendar | null) => {
        calendarRef.current = ref
    }, [])

    const triggerRefetch = useCallback(() => {
        queryClient.invalidateQueries({ queryKey: [ConfigQuery.EVENTS_LIST_BY_DAY] })
        calendarRef.current?.getApi().refetchEvents()
    }, [])

    const value = useMemo(
        () => ({
            setCalendarRef,
            triggerRefetch,
        }),
        [setCalendarRef, triggerRefetch],
    )

    return <RefreshCalendarContext.Provider value={value}>{children}</RefreshCalendarContext.Provider>
}

export function useRefreshCalendar() {
    const context = useContext(RefreshCalendarContext)

    // If the hook is used outside of the Provider, context will be null
    if (!context) {
        throw new Error(
            "useRefreshCalendar must be used within a RefreshCalendarProvider. " +
                "Check that you have wrapped your component " +
                "in <RefreshCalendarProvider ...>.",
        )
    }

    return context
}
